/* eslint-disable no-unused-vars */
/* global G, PLATFORM */
import { asyncPipeSpread, asyncPipeSpreadIf, bulk, getFirstItem, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import map from 'lib/sequence/model/api/map'
import { hidden, hide, show } from 'lib/sequence/component/state/hidden'
import { setPasswordInfo } from 'app/optionManagement/action/create'
import { get, set } from 'lib/sequence/component/state/value'
import showQuickAction from 'app/_shared/events/appbar/quickActions/show'
import hideQuickAction from 'app/_shared/events/appbar/quickActions/hide'
import { isOlderThanOneDay } from 'lib/util/date'
import getPinStatus from 'app/optionManagement/event/getPinStatus'
import getUserRole from 'app/_shared/events/user/role'
import session, { settings } from 'app/_shared/session'

const bulkShow = bulk(show)
const bulkHide = bulk(hide)

/**
 * Returns whether the current code is a pin or not.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {boolean}
 */
const isPin = module => module[G.MODEL][G.CHILDREN]?.type?.[G.CACHE] === 'pin'

/**
 * Maps the current model to the view's info component.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapCodeInfo = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { info } = components

  map(model)(info)

  return [components, ...args]
}

/**
 * Hides all fields below the `info` component.
 *
 * We hide all the fields by default, but will show the fields
 * relevant for the code type (`pin`, `option`) further down.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const hideInfoFields = module => async (components, ...args) => {
  const { info } = components

  const {
    submitDateLabel,
    serialNumber,
    location,
    deviceInformation,
    contact,
    softwareVersion,
  } = find(info)

  bulkHide(submitDateLabel, serialNumber, location, deviceInformation, contact, softwareVersion)

  return [components, ...args]
}

/**
 * Toggles the display of the 'Get password' button so that it's shown only if the current model
 * doesn't have a password set.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayGetPasswordButton = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { password } = model[G.CHILDREN]
  const { component } = components
  const actionsRoot = component[G.ACTIONS][0]

  const btnGetPassword = (actionsRoot[G.CHILDREN] || []).find(a => a._name === 'btnGetPassword')
  const btnDetail = (actionsRoot[G.CHILDREN] || []).find(a => a._name === 'btnDetail')

  password[G.CACHE]
    ? hide(actionsRoot)
    : show(actionsRoot)
      && hide(btnDetail)
      && show(btnGetPassword)

  return [components, ...args]
}

/**
 * Shows the `info` fields relevant for passwords.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const showPasswordInfoFields = module => async (components, ...args) => {
  const { info } = components
  const {
    submitDateLabel,
    location,
    deviceInformation,
    contact,
  } = find(info)

  bulkShow(submitDateLabel, location, deviceInformation, contact)

  return [components, ...args]
}

/**
 * Shows the edit quick action.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const showEditQuickAction = module => async (components, ...args) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  showQuickAction('edit', module, actionComponent, null)

  return [components, ...args]
}

/**
 * Displays the password section by showing the password of the current model, if it has any, or
 * information about the last attempt to obtain it.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
export const displayPasswordSection = module => async (components, ...args) => {
  const intl = module[G.ADAPTER][G.INTL]
  const { response, password } = module[G.MODEL][G.CHILDREN]
  const { passwordInfo, noPasswordInfo, responseText } = components

  password[G.CACHE]
    ? show(passwordInfo) && hide(noPasswordInfo)
    : show(noPasswordInfo) && hide(passwordInfo)

  if (!hidden(noPasswordInfo) && response[G.CACHE]) {
    const { dialogMessageKey, dialogMessage = '' } = response[G.CACHE]
    const dialogMessageTextKey = `${dialogMessageKey}.text`
    const { messageKey: _key = dialogMessageTextKey, message = dialogMessage } = response[G.CACHE]

    if (_key) {
      const messageText = await intl._t(_key, {
        _key,
        ns: 'optionManagement',
        defaultValue: message,
      })
      set(responseText, messageText)
    }
  }

  return args
}

/**
 * Hides the 'Go to machine' button for Options.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const hideDetailButton = module => async (components, ...args) => {
  const { component, btnDetail: detailButton } = components
  const actionsRoot = component[G.ACTIONS][0]

  const detailAction = (actionsRoot[G.CHILDREN] || []).find(a => a._name === 'btnDetail')

  const targetDetailButton = PLATFORM === 'web' ? detailButton : detailAction

  hide(targetDetailButton)

  return [components, ...args]
}

/**
 * Displays the 'Go to machine' button for PINs.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayDetailButton = module => async (components, ...args) => {
  const { user } = session(module)
  const model = module[G.MODEL]
  const online = module[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]

  const { equipment } = model[G.CHILDREN]
  const { component, btnDetail: detailButton } = components
  const actionsRoot = component[G.ACTIONS][0]

  const userRole = getUserRole(user.ref()?.value?.roles, module, component, null)?.key

  const btnGetPassword = (actionsRoot[G.CHILDREN] || []).find(a => a._name === 'btnGetPassword')
  const detailAction = (actionsRoot[G.CHILDREN] || []).find(a => a._name === 'btnDetail')

  const targetDetailButton = PLATFORM === 'web' ? detailButton : detailAction

  const machine = getFirstItem(equipment?.[G.CACHE])

  hide(btnGetPassword)
  bulkShow(actionsRoot, targetDetailButton)

  if (userRole === 'PinCreatorOnly_c') {
    hide(actionsRoot)
  }

  if (machine === -1 && !online) {
    setKey(true, 'disabledWhenOffline', targetDetailButton[G.STATE])
  } else if (machine === null) {
    setKey(true, 'disabled', targetDetailButton[G.STATE])
  }

  return [components, ...args]
}

/**
 * Sets the correct value for the `pinInfo` field.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setPinInfo = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { password } = model[G.CHILDREN]
  const { pin } = components
  const pinValue = password[G.CACHE]

  show(pin)
  set(pin, pinValue || '-')

  return [components, ...args]
}

/**
 * Shows the `info` fields relevant for PINs.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const showPinInfoFields = module => async (components, ...args) => {
  const currentLanguage = module[G.ADAPTER][G.SESSION][G.STATE][G.LANGUAGE]
  const { equipment } = module[G.MODEL][G.CHILDREN]
  const { product } = equipment?.[G.CHILDREN] || {}
  const { value = {} } = getFirstItem(product?.[G.CACHE] || []) || {}
  const localizedNames = value?.['i18n:name']
  const localizedName = localizedNames?.[currentLanguage]

  const { info } = components
  const {
    submitDateLabel,
    deviceInformation,
    serialNumber,
    softwareVersion,
  } = find(info)

  bulkShow(submitDateLabel, serialNumber, deviceInformation, softwareVersion)

  localizedName
    ? set(deviceInformation, localizedName)
    : hide(deviceInformation)

  return [components, ...args]
}

/**
 * Hides the edit quick action.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const hideEditQuickAction = module => async (components, ...args) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  hideQuickAction('edit', module, actionComponent, null)

  return [components, ...args]
}

/**
 * Shows the `pinInfo` section.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayPinSection = module => async (components, ...args) => {
  const intl = module[G.ADAPTER][G.INTL]
  const pinCache = module[G.MODEL][G.CACHE]
  const { response, equipment } = module[G.MODEL][G.CHILDREN]
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]

  const { pinInfo, pin, pinStatus, pinResponseText, noEquipmentText } = components

  const responseCache = response[G.CACHE]

  show(pinInfo)

  // Maybe show a message that we don't have an equipment
  !equipment[G.CACHE] && !settings.suppressEquipmentDevices
    ? show(noEquipmentText)
    : hide(noEquipmentText)

  // Setting messageText according to response error
  if (responseCache.messageKey) {
    const { messageKey, message = '' } = responseCache

    const messageText = await intl._t(messageKey, {
      messageKey,
      ns: 'optionManagement',
      defaultValue: message,
    })

    set(pinResponseText, messageText)
    // Setting messageText if pin outdated
  } else if (isOlderThanOneDay(pinCache?.value?.submitDate)) {
    set(
      pinResponseText,
      await intl._t('optionmanagement.error.pin-outdated', {
        _key: 'optionmanagement.error.pin-outdated',
        ns: 'optionManagement',
        defaultValue: 'The generated PIN is older than 24 hours. Please generate a new PIN.',
      }),
    )
  } else {
    hide(pinResponseText)
  }

  // Getting the current pin status message and setting it
  const currentPinStatus = await getPinStatus(module, actionComponent, null)
  currentPinStatus?.length && set(pinStatus, currentPinStatus)

  // Adding margin between error chip and pin in case both are shown
  get(pin)
    && currentPinStatus?.length
    && setKey(2, 'marginBottom', pinStatus[G.STATE])

  return args
}

/**
 * OptionManagement detail action
 *
 * @param {Gaia.AppModule.Spec} module  current module
 */
export default module => component => async (...args) => asyncPipeSpread(
  mapCodeInfo(module),
  hideInfoFields(module),
  asyncPipeSpreadIf(() => !isPin(module))(
    hideDetailButton(module),
    displayGetPasswordButton(module),
    setPasswordInfo(module),
    showPasswordInfoFields(module),
    showEditQuickAction(module),
    displayPasswordSection(module),
  ),
  asyncPipeSpreadIf(() => isPin(module))(
    displayDetailButton(module),
    setPinInfo(module),
    showPinInfoFields(module),
    hideEditQuickAction(module),
    displayPinSection(module),
  ),
)(find(component), ...args)
